import { useState, type ReactNode } from "react";
import type { TFunction } from "i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { client } from "../lib/api";
import { useOnboardingSession } from "../areas/onboarding/hooks/onboarding-queries";
import { useProductionPermissions } from "../areas/office/production/hooks/permissions";
import { useAgentsPermissions } from "../areas/office/agents/hooks/permissions";
import { useAgentsNav } from "../areas/office/agents/hooks/agents-queries";
import {
  floorPendingWorkCount,
  resetFloorDeviceState,
} from "../areas/floor-pwa/hooks/use-floor-outbox";
import { setFloorPrincipal } from "../areas/floor-pwa/lib/floor-principal";
import { LocaleSwitcher } from "../components/locale-switcher";
import { resetProfileRead } from "../lib/profile-locale";
import { resetRefNames } from "../lib/ref-names";
import { resetRoleFeed, useRoleCodesFeed } from "../lib/role-codes";
import { officeAreas } from "./office-areas";
import { Button } from "../components/button";
import { guideHref, guidePageFor } from "../lib/guide-links";
import { AppShell, type ShellNavItem } from "../components/app-shell";
import {
  IconAgents,
  IconAudit,
  IconCatalogue,
  IconFloor,
  IconHome,
  IconInventory,
  IconMessaging,
  IconPack,
  IconPlanning,
  IconProduction,
  IconQuality,
  IconSales,
  IconSetup,
  IconTerms,
  IconWaste,
} from "../components/icons";
import {
  isStagingHost,
  shellCountLabel,
  tenantLabel,
  useOpenWorkOrderCount,
  type ShellCount,
} from "./shell-data";
import { AdminChrome } from "../areas/admin/admin-shell";
import { isAdminHost, isAdminOnlyPath } from "../lib/host";
import { ReadOnlyBoundary } from "../components/read-only";
import { useTenantSuspension } from "../lib/tenant-suspension";
import {
  GraceBanner,
  graceSentence,
  SuspendedScreen,
  useSuspensionFromProfile,
  WorkspaceClosedScreen,
} from "./suspension";
import { NotFoundPage } from "./not-found";
import { PublicLocaleBoundary } from "../areas/public/public-shell";
import { ImpersonationBanner } from "./impersonation-banner";

/**
 * Sign-out on a SHARED FLOOR DEVICE.
 *
 * Signing out used to leave the outbox and the cached worksheets on the device.
 * Two consequences, both real on a tablet that changes hands every shift: the
 * next operator's session drains the previous one's queued cut and the ledger
 * records the wrong actor, and the previous operator's cached count values and
 * serials render for someone with no grant for them.
 *
 * So the queue is purged with the read cache — but never SILENTLY while it still
 * holds work. Unsent work is named, and the operator decides: stay signed in and
 * find signal, or sign out and lose it. There is no third option that is honest.
 */
/** How long sign-out waits for the server before leaving anyway. */
const LOGOUT_NETWORK_BUDGET_MS = 3_000;

export function LogoutButton() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const logout = useMutation({
    // ALWAYS, not "online". React Query's default `networkMode: "online"`
    // PAUSES a mutation while `navigator.onLine` is false and runs it when
    // connectivity returns — so on a floor phone in a dead zone the sign-out
    // simply never happened: no request, no `onSettled`, no navigation, and the
    // outgoing operator was trapped on a signed-in screen with the incoming one
    // waiting. Sign-out is a LOCAL act that happens to tell the server; it must
    // never be gated on the network. (Found by the M06-S13 hand-over leg.)
    networkMode: "always",
    mutationFn: async () => {
      // SIGNING OUT MUST NOT NEED A NETWORK. A shift changes at the back of the
      // plant, which is precisely where there is no signal, and a request that
      // never settles used to trap the outgoing operator on a signed-in screen
      // with the incoming one waiting. The server call is best-effort and is
      // raced against a short deadline; the LOCAL half — dropping the session
      // cookie's usefulness by leaving, purging the queue and the cached reads
      // — is what actually protects the next person, and it always runs.
      await Promise.race([
        client.POST("/v1/auth/logout").catch(() => undefined),
        new Promise((resolve) => setTimeout(resolve, LOGOUT_NETWORK_BUDGET_MS)),
      ]);
    },
    onSettled: () => {
      setFloorPrincipal(undefined);
      // The next person on this device reads their own profile and language,
      // and asks for every name again under their own grants (spec 16 §3.13
      // threat 6): the QueryClient outlives this in-app navigation.
      resetProfileRead();
      resetRefNames(queryClient);
      resetRoleFeed(queryClient);
      void resetFloorDeviceState(queryClient).finally(() => {
        void navigate({ to: "/login" });
      });
    },
  });

  const pending = floorPendingWorkCount();

  if (confirming) {
    return (
      <>
        <span className="nav-warning" role="alert" data-testid="logout-pending-warning">
          {t("nav.logoutPending", { count: pending })}
        </span>
        <Button
          onClick={() => {
            setConfirming(false);
            logout.mutate();
          }}
          data-testid="logout-confirm"
        >
          {t("nav.logoutAnyway")}
        </Button>
        <Button
          onClick={() => {
            setConfirming(false);
          }}
        >
          {t("nav.staySignedIn")}
        </Button>
      </>
    );
  }

  return (
    <Button
      onClick={() => {
        if (pending > 0) {
          setConfirming(true);
          return;
        }
        logout.mutate();
      }}
      data-testid="logout"
    >
      {t("nav.logout")}
    </Button>
  );
}

/**
 * The nav's live count slot (routes/shell-data.ts decides the three states).
 *
 * `ShellNavItem.count` is typed `number`, so it cannot carry the OVERFLOW form,
 * and before 2026-08-20 the sidebar simply LOST its number the moment a tenant
 * passed one page (200 rows) — silently, permanently, and at exactly the size
 * where the number starts to matter. The label therefore goes through the
 * shell's existing `chip` slot wearing the same `ds-nav-ct` class the count slot
 * renders: identical DOM, identical geometry (the class is `margin-left:auto`,
 * so the row looks the same and reserves the same space), and ONE mechanism for
 * every count state rather than two that diverge at 200 rows.
 *
 * HANDOVER, not an accident: the tidier end state is widening
 * `ShellNavItem.count` to `string | undefined` in components/app-shell.tsx and
 * deleting this helper. That file is outside this change's ownership boundary
 * (other agents are writing in this tree), so it is reported, not edited.
 */
function navCount(count: ShellCount, t: TFunction): ReactNode {
  const label = shellCountLabel(count, t);
  // Absence, not a dash: a failed count read is not something the CHROME tells
  // anyone about (the onboarding score chip beside it drops on failure for the
  // same reason), and the row is unchanged without it. The home KPI tile is the
  // surface that has to say "unknown" out loud, and it does.
  return label === undefined ? undefined : <span className="ds-nav-ct">{label}</span>;
}

/**
 * The office nav MODEL (design-system spec §3.1). One list, permission-gated
 * exactly as the pre-UX-0 nav components were:
 *   - Set up / Terminology on the module-02 §7 mirror (`onboarding.view` /
 *     `onboarding.edit_terminology`); the wizard entry carries the score chip
 *     while the tenant is not live.
 *   - Production on `production.read` (spec 04 §4, slice 04-A).
 *   - Waste on the §5 row 57 settings read (spec 05 §4 S11).
 *   - Floor on the module-06 floor grants.
 * Every gate reads the caller's own permission list and role lineage off
 * `GET /v1/auth/tenants` (D-586), so a tenant's clone of a seed role sees the
 * areas its source role sees, and a role the tenant trimmed sees fewer. The
 * reads are fail-closed, so nothing flashes before roles resolve, and the
 * server refuses regardless — this only decides what is offered.
 *
 * Live counts come from the modules' existing list reads (shell-data.ts). They
 * are never faked (D-033) and never collapsed: an exact count renders as itself,
 * a count whose page could not finish renders as a true lower bound ("200+"),
 * and a count nothing could answer renders as nothing at all — see `navCount`.
 */
function useOfficeNav(): { readonly items: readonly ShellNavItem[]; readonly settled: boolean } {
  const { t } = useTranslation();
  // ONE rule for which areas show, read off the role feed (D-586):
  // `officeAreas` in ./office-areas.ts, unit-tested against every seed role,
  // a clone of each and a trimmed clone of each.
  const feed = useRoleCodesFeed();
  const areas = new Map(
    officeAreas(feed, { feedFailed: feed.isFeedError }).map((area) => [area.key, area.to]),
  );
  const agentsPerms = useAgentsPermissions();
  const agentsNav = useAgentsNav(agentsPerms.canList, agentsPerms.isResolved);
  // Never retried into an error card in the chrome — a failed read simply
  // drops the score chip (and the count hooks answer absence the same way).
  const session = useOnboardingSession();
  const canProduction = areas.has("production");
  const openWoCount = useOpenWorkOrderCount(canProduction);

  const items: ShellNavItem[] = [];
  items.push({
    key: "home",
    to: "/home",
    label: t("nav.home"),
    icon: <IconHome />,
    testid: "nav-home",
  });

  if (areas.has("onboarding")) {
    const row = session.data?.session;
    const summary = session.data?.checklist_summary;
    const showScore = row !== undefined && row.status !== "live" && summary !== undefined;
    items.push({
      key: "onboarding",
      to: "/onboarding",
      label: t("nav.onboarding"),
      icon: <IconSetup />,
      testid: "nav-onboarding",
      chip: showScore ? (
        <span className="nav-chip ds-nav-ct" aria-label={t("onboarding.score.label")}>
          {t("nav.setupScore", { score: summary.score })}
        </span>
      ) : undefined,
    });
  }

  // ONE catalogue entry, not two. The nav carried "Products" (the retired P0
  // skeleton table at `/products`) directly above "Catalogue" (the Module 01
  // list at `/catalogue/products`) — two links, two labels, the same subject,
  // and the first of them a screen you could not click through. Both owner
  // reports came out of that pair. The skeleton pages are retired; the entry
  // that survives is the one that owns the module.
  //
  // Gated on `catalogue.read`, which every seed role holds, so the entry shows
  // for every seed role exactly as before; a tenant's role without the read
  // no longer gets a link into a 403 (D-586).
  if (areas.has("catalogue")) {
    items.push({
      key: "catalogue",
      to: "/catalogue/products",
      label: t("nav.catalogue"),
      icon: <IconCatalogue />,
      testid: "nav-catalogue",
    });
  }
  // Module 06's office area, on `inventory.read`, which every seed role holds;
  // the area's own screens gate themselves beyond it (D-586).
  if (areas.has("inventory")) {
    items.push({
      key: "inventory",
      to: "/inventory/lookup",
      label: t("nav.inventory"),
      icon: <IconInventory />,
      testid: "nav-inventory",
    });
  }

  if (canProduction) {
    items.push({
      key: "production",
      to: "/production/work-orders",
      label: t("nav.production"),
      icon: <IconProduction />,
      testid: "nav-production",
      chip: navCount(openWoCount, t),
    });
  }

  // Module 05's office area. The link is offered to ANY role the area holds a
  // screen for and points at the FIRST screen that role can open — both facts
  // come from `wasteAreaEntries`, the one table the sub-nav also renders.
  //
  // It used to be gated on `canReadSettings` (group_admin / facility_admin /
  // planner) and pointed at `/waste/settings`. That left finance — whom §4 S2
  // names first, and who holds `waste.read`, `waste.event.reverse`,
  // `waste.disposition.set` and `waste.cost.manual` — with no way into the
  // ledger; qc (`waste.read`) with none; and storekeeper and sales
  // (`waste.remnant.manage`) with no way to the remnant registry that exists
  // for them. Planner's one link went to the catalogue editor.
  const wasteTo = areas.get("waste");
  if (wasteTo !== undefined) {
    items.push({
      key: "waste",
      to: wasteTo,
      label: t("nav.waste"),
      icon: <IconWaste />,
      testid: "nav-waste",
    });
  }

  // Module 07's office area (slice 07-A). Same one-table rule as waste: the
  // link is offered to ANY role the area holds a screen for and points at the
  // FIRST screen that role can open — `qualityAreaEntries`, which the sub-nav
  // also renders. `maintenance` holds no `quality.*` code at all (§7's cells
  // are "—" across the row), so it gets no link rather than a link into a 403.
  const qualityTo = areas.get("quality");
  if (qualityTo !== undefined) {
    items.push({
      key: "quality",
      to: qualityTo,
      label: t("nav.quality"),
      icon: <IconQuality />,
      testid: "nav-quality",
    });
  }

  // Module 09's office area (slice P3-A part 2). Same one-table rule as waste
  // and quality: the link is offered to ANY role the area holds a screen for
  // and points at the FIRST screen that role can open — `salesAreaEntries`,
  // which the sub-nav and the `/sales` index redirect also read.
  //
  // The audiences are ROLE SETS where §4's are (§4.1's customer book admits
  // ga/fa/sales/finance/planner — narrower than the `sales.read` holder set,
  // because qc and storekeeper hold that code for the quote/order read-only
  // and ship-queue surfaces P3-B/P3-C ship, not for the customer book). So qc,
  // storekeeper, procurement, maintenance and floor_worker get no link rather
  // than a link into a 403.
  const salesTo = areas.get("sales");
  if (salesTo !== undefined) {
    items.push({
      key: "sales",
      to: salesTo,
      label: t("nav.sales"),
      icon: <IconSales />,
      testid: "nav-sales",
    });
  }

  // Module 17's office area (slice P3-B part 2). Same one-table rule: the
  // link is offered to ANY role the area holds a screen for and points at the
  // FIRST screen that role can open — `messagingAreaEntries`, which the
  // sub-nav and the `/messaging` index redirect also read. sales/fa/ga land
  // on the inbox working it; planner, finance and qc land on it read-only
  // (they hold `messaging.conversation.read`); §4.4's settings entry exists
  // only inside the area's own sub-nav for ga/fa, per its "nav item hidden"
  // rule. Everyone else gets no link rather than a link into a 403.
  const messagingTo = areas.get("messaging");
  if (messagingTo !== undefined) {
    items.push({
      key: "messaging",
      to: messagingTo,
      label: t("nav.messaging"),
      icon: <IconMessaging />,
      testid: "nav-messaging",
    });
  }

  // Module 03's office area: each screen reads on its own code (D-390), so the
  // link lands on the first entry the reader holds. Maintenance, with the
  // calendar read alone, lands on the calendars screen.
  const planningTo = areas.get("planning");
  if (planningTo !== undefined) {
    items.push({
      key: "planning",
      to: planningTo,
      label: t("nav.planning"),
      icon: <IconPlanning />,
      testid: "nav-planning",
    });
  }

  // Module 14's office area (D-529 ruling 1): rendered only when the tenant has
  // at least one agent row this reader may see, which only `GET /v1/agents`
  // can say. A reader who may not ask, a refusal and a failed read all render
  // no section (the copy budget's rule). The link lands on the Hub, which every
  // reader the list answers may open (read-only for `agents.invoke`).
  if (agentsNav.visible) {
    items.push({
      key: "agents",
      to: "/agents",
      label: t("agents.nav.section"),
      icon: <IconAgents />,
      testid: "nav-agents",
    });
  }

  // The AUDIT TRAIL (spec 16 §2.16/§4). Secondary, beside the other
  // administration entries: it is the screen a Big Boss opens on purpose — for
  // a customer audit, a dispute, or "who released that quarantine?" — never one
  // he arrives at.
  //
  // Gated on `identity.view_auth_events`, which only group_admin and
  // facility_admin hold. The area's SECOND gate (group scope) cannot be
  // evaluated here — `/v1/auth/tenants` carries role codes and no scope — so a
  // facility-scoped facility_admin sees the link and the screen renders the
  // server's own group-scope refusal with its own sentence. Hiding the link
  // would need the grant's scope on that feed, which D-586 left out.
  if (areas.has("audit")) {
    items.push({
      key: "audit",
      to: "/audit",
      label: t("audit.nav.trail"),
      icon: <IconAudit />,
      testid: "nav-audit",
      secondary: true,
    });
  }

  if (areas.has("terminology")) {
    items.push({
      key: "terminology",
      to: "/settings/terminology",
      label: t("nav.terminology"),
      icon: <IconTerms />,
      testid: "nav-terminology",
      secondary: true,
    });
  }

  // Settings · Industry pack (D-343): the previewed pack update, group_admin's
  // (`onboarding.install_pack`), the only door a live tenant has to a newer pack.
  if (areas.has("industry-pack")) {
    items.push({
      key: "industry-pack",
      to: "/settings/industry-pack",
      label: t("nav.industryPack"),
      icon: <IconPack />,
      testid: "nav-industry-pack",
      secondary: true,
    });
  }

  // The floor entry: offered to anyone holding a floor grant — a floor_worker
  // has no other way in, and without a link the whole slice is URL-only.
  if (areas.has("floor")) {
    items.push({
      key: "floor",
      to: "/floor",
      label: t("nav.floor"),
      icon: <IconFloor />,
      testid: "nav-floor",
      secondary: true,
    });
  }

  return { items, settled: agentsNav.settled };
}

/** The office/onboarding chrome: AppShell around the routed screen. */
function OfficeShell() {
  const { t, i18n } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();
  const { items: nav, settled: agentsSettled } = useOfficeNav();
  // All four §7 mirrors read the ONE role feed (AUTH_TENANTS_QUERY_KEY,
  // staleTime ∞), so a single isResolved covers the whole nav decision — the
  // §5 render-once gate. Feed error still resolves: fail-closed absences.
  // The Agents section also waits on its one list read (D-529 ruling 1), so
  // the nav paints once, complete, rather than growing a row afterwards.
  const navReady = useProductionPermissions().isResolved && agentsSettled;
  const hostname = window.location.hostname;

  return (
    <AppShell
      nav={nav}
      navReady={navReady}
      tenant={tenantLabel(hostname)}
      envChip={isStagingHost(hostname) ? t("nav.envStaging") : undefined}
      brandName={t("nav.brandName")}
      brandSub={t("nav.brandSub")}
      searchLabel={t("nav.searchLabel")}
      searchPlaceholder={t("nav.searchPlaceholder")}
      phoneMore={{
        label: t("nav.more"),
        title: t("nav.moreTitle"),
        closeLabel: t("nav.moreClose"),
      }}
      onSearch={(q) => {
        void navigate({ to: "/catalogue/products", search: { q } });
      }}
      // A plain link: /guide is the API's, not the router's (guide §7). It
      // opens the page for the screen in hand, or the reader's role section.
      help={{ href: guideHref(i18n.language, guidePageFor(pathname)), label: t("nav.help") }}
      topRight={
        <>
          <LocaleSwitcher />
          <LogoutButton />
        </>
      }
    >
      {/* S19 (M15-8): who from Assessly is using this session, with End. */}
      <ImpersonationBanner />
      <SuspensionFrame>
        <Outlet />
      </SuspensionFrame>
    </AppShell>
  );
}

/**
 * The grace window's frame around every office and floor screen (spec 15
 * §3.2, D-495 ruling 2): the red read-only banner above, and the read-only
 * boundary that stops the shared submit button. It also reads the signed-in
 * profile's window once, so both are up before the first refused write.
 */
function SuspensionFrame({ children, floor = false }: { children: ReactNode; floor?: boolean }) {
  const { t, i18n } = useTranslation();
  useSuspensionFromProfile();
  const suspension = useTenantSuspension();
  if (suspension.kind !== "grace") return <>{children}</>;
  return (
    <ReadOnlyBoundary reason={graceSentence(suspension, t, i18n.language)}>
      <GraceBanner state={suspension} floor={floor} />
      {children}
    </ReadOnlyBoundary>
  );
}

/**
 * S12's route (spec 04 §4 floor row 12). Named here because the CHROME
 * decision belongs to the shell, not to the screen.
 */
export const WALL_PATH = "/wall";

export function isWallPath(pathname: string): boolean {
  return pathname === WALL_PATH;
}

/**
 * The §4.10 public doors: `/q/{token}` and `/track/{public_token}`.
 *
 * PURE AND EXPORTED so the shell decision is unit-testable without a router —
 * the same shape as `isWallPath`, for the same reason: the wall board's chrome
 * regression (five nav links and a Sign out button on a corridor tablet) was
 * only catchable once the decision was a function.
 *
 * `/q/wc/…` and `/q/op/…` are the floor's QR resolvers and must NOT match:
 * they are signed-in floor screens and belong in the floor shell. So the test
 * is on segment COUNT, mirroring the router's own ranking — `/q/x` is a public
 * quote, `/q/wc/x` is a scan. A prefix test (`pathname.startsWith("/q/")`)
 * would have stripped the floor chrome off both scan resolvers silently.
 */
export function isPublicDoorPath(pathname: string): boolean {
  const parts = pathname.split("/").filter((p) => p.length > 0);
  if (parts.length !== 2) return false;
  return parts[0] === "q" || parts[0] === "track";
}

/**
 * THE WALL BOARD'S SHELL — no header, no nav, no locale switcher, no sign-out.
 *
 * S12 is furniture: a tablet taped to a corridor wall that nobody signs into
 * and nobody taps. Its spec row and its own file header both say it carries NO
 * CONTROLS AT ALL, and the reason is not tidiness — a control on a wall board
 * is a control a passing shoulder knocks. It was first routed off `rootRoute`
 * to escape the FLOOR shell (back link, sync banner), which was right, but
 * `rootRoute` renders the OFFICE shell, which is strictly worse: it put five
 * nav links and a `Sign out` button on the glass, so one brushed elbow logged
 * the corridor board out and the plant lost its board until somebody with
 * credentials walked over. Measured in a real browser: 7 interactive controls.
 *
 * There is only one root route in this router, so "its own layout" is a branch
 * here rather than a second root — and a branch is the stronger form, because
 * the control count is then a property of the SHELL and is asserted as zero by
 * `wall-board.test.tsx` rendering this component, not by reading the JSX.
 */
function WallShell() {
  return (
    <div className="app app-wall">
      <main className="app-main">
        <Outlet />
      </main>
    </div>
  );
}

export function RootLayout() {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // Read before any branch so hook order never depends on the path.
  const suspension = useTenantSuspension();
  // HOST BEFORE PATH. `admin.<apex>` is the platform console (spec 15 §4) and
  // is a DIFFERENT APPLICATION on the same bundle: no tenant to resolve, no
  // office chrome, its own API surface (`/v1/platform/*`, which answers on this
  // host and nowhere else). Everything below this line assumes a tenant host.
  //
  // This is why the invite link in the bootstrap email — `https://admin.<apex>/
  // accept-invite?token=…` — used to land nowhere: the host served the tenant
  // office SPA, which has no such route and cannot resolve a tenant from it.
  if (isAdminHost(window.location.hostname)) return <AdminChrome />;
  // The mirror of that rule: a TENANT host must never serve a console screen.
  // The routes exist in the one router (one bundle), so without this an office
  // user who followed a stale console link would get the enrolment screen
  // wired to endpoints their host 404s. They get the designed 404 instead.
  if (isAdminOnlyPath(pathname)) {
    return (
      <div className="app">
        <header className="app-header">
          <span className="app-title">{t("app.title")}</span>
        </header>
        <main className="app-main">
          <NotFoundPage />
        </main>
      </div>
    );
  }
  // FURNITURE FIRST: before any nav decision, because the wall board's
  // requirement is the absence of chrome rather than a narrower selection of it.
  if (isWallPath(pathname)) return <WallShell />;
  // THE PUBLIC DOORS (spec 09 §4.10, slice P3-C-1) — before every shell
  // decision below, and before anything that reads the role feed.
  //
  // These two paths are opened by a CUSTOMER: no session, no tenant
  // membership, no role codes, and no business being offered a nav to screens
  // they cannot open or a "Sign out" for a session they do not have. They
  // wear their own shell (areas/public/public-shell.tsx), which is the
  // `/accept-invite` and `/wall` precedent — the CHROME decision belongs to
  // the route, and it is asserted as a property of a rendered component
  // rather than read out of this JSX.
  //
  // The branch is by PATH PREFIX and not by "is there a session", because a
  // signed-in manager opening their own customer's link must get the same
  // customer-facing page the customer gets — otherwise nobody at the factory
  // can ever see what they sent.
  // …and it renders against a LOCALE-SCOPED i18next instance (ledger row 89).
  // The public door writes §4.10's document locale, and i18next's language is
  // process-wide: a signed-in manager who opened a customer's link in the same
  // tab had their OFFICE app switch language, and it stayed switched after they
  // navigated back. This is the one node above BOTH public screens that already
  // knows the render is a public door — and it has to be above them, because
  // each screen calls `useTranslation()` in its own body and passes resolved
  // strings down, so a provider inside `PublicShell` would sit below nearly
  // every word a customer reads.
  if (isPublicDoorPath(pathname))
    return (
      <PublicLocaleBoundary>
        <Outlet />
      </PublicLocaleBoundary>
    );
  // The floor area wears its own shell. Five office links a floor_worker cannot
  // use — and will 403 on — eat the top of a 360 px screen before the sticky
  // sync banner starts, and the desktop `.app-main` margins push the banner
  // further down still. `.app-floor` nulls those out (areas/floor-pwa/screens/floor-inventory.css).
  const onFloor = pathname === "/floor" || pathname.startsWith("/floor/");
  const authSurface =
    pathname === "/login" ||
    pathname === "/" ||
    pathname === "/forgot-password" ||
    pathname === "/reset" ||
    // S19's landing (M15-8): the console's one-time support link.
    pathname === "/impersonate";
  // A locked or closed tenant renders no screen of its own (spec 15 §4 S20,
  // D-495): the office and the floor show the lock screen instead. The sign-in
  // card keeps its own form and says it there (routes/login.tsx).
  if (!authSurface && suspension.kind === "locked") {
    return <SuspendedScreen category={suspension.category} signOut={<LogoutButton />} />;
  }
  if (!authSurface && suspension.kind === "purged") return <WorkspaceClosedScreen />;
  if (onFloor) {
    return (
      <div className="app app-floor">
        <ImpersonationBanner />
        <header className="app-header" />
        <main className="app-main">
          <SuspensionFrame floor>
            <Outlet />
          </SuspensionFrame>
        </main>
      </div>
    );
  }
  // The auth surfaces and the index redirect: a centered card on the canvas,
  // no chrome — there is nothing to navigate to before a session exists. The
  // reset pair (UX-6a) rides the same canvas: `/reset` is where the API's
  // reset email points, `/forgot-password` is the login card's own link.
  if (authSurface) {
    return (
      <div className="app">
        <header className="app-header">
          <span className="app-title">{t("app.title")}</span>
        </header>
        <main className="app-main">
          <Outlet />
        </main>
      </div>
    );
  }
  // Every office/onboarding screen renders inside the ONE AppShell (§3.1).
  return <OfficeShell />;
}
